import type { jsPDF } from 'jspdf';
import { RiskBand, RiskByYearMonthDistrict, ReportFilters, MONTH_FULL_NAMES } from '../../../types/types';
import { computeAreaRiskMetrics, getRiskBand, resolveRiskSnapshot, resolveYearRiskSnapshot } from '../../../utils/summary_report/metrics/areaRiskMetrics';
import {
  drawSectionHeader,
  ensureSpace,
  drawStatCard,
  measureStatCardLabel,
  computeStatCardHeight,
  drawHorizontalBarChart,
  drawLegend,
  Rect,
  STAT_CARD_ICON_RESERVED_WIDTH,
  STAT_CARD_ICON_IMAGE_RESERVED_WIDTH,
} from '../../../utils/summary_report/pdfPrimitives';
import { drawDistrictMap } from '../districtMap';
import { GeoFeatureCollection } from '../geojsonUtils';
import { colors, fonts, layout } from '../../../theme/themes';
import { pinIcon, houseIcon, warningIcon } from '../../../utils/summary_report/icon';

function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

/**
 * District names occasionally drift between the risk-data API and the
 * GeoJSON boundary file (spelling, not just casing — e.g. the risk API's
 * "Hiliwa" vs. the GeoJSON's "HELIWA"). Case-insensitive matching handles
 * casing automatically; anything here bridges a genuine spelling
 * difference. UNCONFIRMED: verify these are actually the same district
 * before relying on it for a real report.
 */
const DISTRICT_NAME_ALIASES: Record<string, string> = {
  heliwa: 'hiliwa', // GeoJSON spells it "HELIWA"; risk API spells it "Hiliwa"
};

/** Case-insensitive (and alias-aware) lookup from a GeoJSON district name
 * to its risk stats, since the two data sources don't always agree on
 * exact spelling/casing. */
function buildDistrictLookup(snapshot: ReturnType<typeof resolveRiskSnapshot>['snapshot']) {
  const byLowerName = new Map<string, string>();
  for (const key of Object.keys(snapshot)) {
    byLowerName.set(key.toLowerCase(), key);
  }
  return (geojsonName: string) => {
    const lower = geojsonName.toLowerCase();
    const direct = byLowerName.get(lower);
    if (direct) return snapshot[direct];
    const aliased = DISTRICT_NAME_ALIASES[lower];
    if (aliased) {
      const aliasedKey = byLowerName.get(aliased);
      if (aliasedKey) return snapshot[aliasedKey];
    }
    return undefined;
  };
}

const BAND_COLOR = {
  extreme: colors.extreme700,
  high: colors.high700,
  medium: colors.medium700,
  low: colors.low700,
} as const;

/**
 * Placeholder map slot — used whenever geojson isn't provided, or the
 * resolved period has no risk data on record at all.
 */
function drawMapPlaceholder(doc: jsPDF, rect: Rect, message: string): void {
  doc.setDrawColor(...colors.stone200);
  doc.setLineDashPattern([3, 2], 0);
  doc.setLineWidth(0.7);
  doc.roundedRect(rect.x, rect.y, rect.width, rect.height, 4, 4, 'D');
  doc.setLineDashPattern([], 0);

  const centerX = rect.x + rect.width / 2;
  const centerY = rect.y + rect.height / 2;

  setPinIcon(doc, centerX, centerY - 18);

  doc.setFont(fonts.body, 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...colors.stone500);
  const w1 = doc.getTextWidth(message);
  doc.text(message, centerX - w1 / 2, centerY + 4);
}

function setPinIcon(doc: jsPDF, cx: number, cy: number): void {
  doc.setDrawColor(...colors.stone400);
  doc.setLineWidth(1);
  doc.circle(cx, cy, 6, 'D');
  doc.setFillColor(...colors.stone400);
  doc.triangle(cx - 4, cy + 4, cx + 4, cy + 4, cx, cy + 12, 'F');
}

/**
 * Builds the section subtitle reflecting exactly which snapshot is being
 * shown, including the "fell back to latest available" case for
 * `mode: 'year'` with no month selected, and distinguishing "nothing at
 * all this year" from "this year has data, just not this month."
 */
function buildSubtitle(
  filters: ReportFilters,
  resolvedMonth: string | null,
  firsMonth: string | null,
  reason: 'ok' | 'no-year-data' | 'no-month-data',
): string {
  if (reason === 'no-year-data') {
    return `No risk data on record for ${filters.year}`;
  }
  if (reason === 'no-month-data') {
    const monthLabel = filters.month ? MONTH_FULL_NAMES[filters.month] : '';
    return `No risk data recorded for ${monthLabel} ${filters.year}`;
  }

  const range = firsMonth && firsMonth !== resolvedMonth ? `${firsMonth}-${resolvedMonth}` : `${resolvedMonth}`
  return `Aggregated across ${range} ${filters.year}`;
}

/**
 * Draws section 02 (areas at risk) starting at `startY` and returns the
 * y-coordinate the next section should start at.
 *
 * Unlike eviction counts, risk data is a stock, not a flow: a selected
 * month is read directly (never summed for "year to date"), and
 * `mode: 'year'` with no month falls back to the most recent month on
 * record for that year — see resolveRiskSnapshot for the full reasoning.
 */
/**
 * Draws a "district: catchment area, catchment area" list, sorted by risk
 * (highest individualsAtRisk first) so truncation — if the list is longer
 * than `maxHeight` — drops the least-relevant districts, not arbitrary
 * ones. Returns the height actually used, so the caller can size its
 * container after the fact rather than guessing up front.
 */
function drawCatchmentAreasList(
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  maxHeight: number,
  entries: { district: string; individualsAtRisk: number; areas: string[] }[],
): number {
  const districtColWidth = 92;
  const areasColWidth = width - districtColWidth - 8;
  const lineHeight = 10.5;
  const rowGap = 5;

  const sorted = [...entries].sort((a, b) => b.individualsAtRisk - a.individualsAtRisk);

  let cursorY = y;
  let shown = 0;

  for (const entry of sorted) {
    const areasText = entry.areas.length > 0 ? entry.areas.join(', ') : 'N/A';
    doc.setFont(fonts.body, 'normal');
    doc.setFontSize(8);
    const wrapped = doc.splitTextToSize(areasText, areasColWidth) as string[];
    const rowHeight = Math.max(1, wrapped.length) * lineHeight;

    // Reserve room for a trailing "+N more" note if this row wouldn't fit.
    const remaining = sorted.length - shown;
    const wouldOverflow = cursorY + rowHeight > y + maxHeight - (remaining > 1 ? 12 : 0);
    if (wouldOverflow) break;

    doc.setFont(fonts.body, 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...colors.stone700);
    doc.text(entry.district, x, cursorY + lineHeight - 2);

    doc.setFont(fonts.body, 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...colors.stone500);
    wrapped.forEach((line, i) => {
      doc.text(line, x + districtColWidth, cursorY + lineHeight - 2 + i * lineHeight);
    });

    cursorY += rowHeight + rowGap;
    shown += 1;
  }

  const hiddenCount = sorted.length - shown;
  if (hiddenCount > 0) {
    doc.setFont(fonts.body, 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...colors.stone400);
    doc.text(`+ ${hiddenCount} more district${hiddenCount === 1 ? '' : 's'} not shown`, x, cursorY + 6);
    cursorY += 14;
  }

  return cursorY - y;
}

/**
 * Draws section 02 (areas at risk) starting at `startY` and returns the
 * y-coordinate the next section should start at.
 *
 * Unlike eviction counts, risk data is a stock, not a flow: a selected
 * month is read directly (never summed for "year to date"), and
 * `mode: 'year'` with no month falls back to the most recent month on
 * record for that year — see resolveRiskSnapshot for the full reasoning.
 */
export function drawAreasAtRiskSection(
  doc: jsPDF,
  data: RiskByYearMonthDistrict,
  filters: ReportFilters,
  startY: number,
  contentWidth: number,
  geojson?: GeoFeatureCollection,
  icons?: { districts?: string; totalHouseholds?: string; extreme?: string },
): number {
  const x = layout.pageMarginX;
  const { snapshot, resolvedMonth, firstMonth, reason } = resolveRiskSnapshot(data, filters);
  
  const mapSnapshot = resolveYearRiskSnapshot(data, filters);
  const metrics = computeAreaRiskMetrics(mapSnapshot);

  
  console.log('resolved', resolvedMonth, firstMonth, reason);
  console.log('snapshot districts', Object.entries(snapshot).map(([d, s]) => [d, s.total_individuals_at_risk]));
  console.log('geojson names', geojson?.features.map((f: any) => f.properties?.name ?? f.properties));

  // --- Compute every size this section needs BEFORE drawing anything, so
  // we can check up front whether the whole section fits on the current
  // page (ensureSpace) — moving header + cards + box together to a new
  // page if not, rather than letting the box silently draw past the edge. ---
  const cardGap = layout.cardGap;
  const cardWidth3 = (contentWidth - cardGap * 2) / 3;

  const districtsLabel = 'Districts with IDPs at risk of eviction';
  const totalHouseholdsLabel = 'Total households at risk';
  const extremeLabel = 'Extreme cases(Eviction notice issued)';
  // 3-word placeholder — swap for whatever wording you actually want here.
  const extremeDescription = 'No tenure extension option';

  const districtsReserved = icons?.districts ? STAT_CARD_ICON_IMAGE_RESERVED_WIDTH : STAT_CARD_ICON_RESERVED_WIDTH;
  const totalHouseholdsReserved = icons?.totalHouseholds ? STAT_CARD_ICON_IMAGE_RESERVED_WIDTH : STAT_CARD_ICON_RESERVED_WIDTH;
  const extremeReserved = icons?.extreme ? STAT_CARD_ICON_IMAGE_RESERVED_WIDTH : STAT_CARD_ICON_RESERVED_WIDTH;
  const districtsMeasurement = measureStatCardLabel(doc, districtsLabel, cardWidth3, districtsReserved);
  const totalHouseholdsMeasurement = measureStatCardLabel(doc, totalHouseholdsLabel, cardWidth3, totalHouseholdsReserved);
  const extremeMeasurement = measureStatCardLabel(doc, extremeLabel, cardWidth3, extremeReserved);
  const tallestMeasurement = [districtsMeasurement, totalHouseholdsMeasurement, extremeMeasurement].reduce(
    (tallest, m) => (m.lines.length * m.lineHeight > tallest.lines.length * tallest.lineHeight ? m : tallest),
  );
  const statCardHeight = computeStatCardHeight(tallestMeasurement, true); // true: reserve space for extreme's description on all 3, so the row stays aligned

  const boxPadding = 16;
  const colGap = 18;
  const leftColWidth = (contentWidth - boxPadding * 2 - colGap) * 0.44;
  const rightColWidth = contentWidth - boxPadding * 2 - colGap - leftColWidth;
  const top5RowHeight = 22;
  const top5ChartHeight = Math.max(1, metrics.top5.length) * top5RowHeight;
  const top5HeaderHeight = 16;
  const catchmentMaxHeight = 256; // cap so a long catchment list can't run away — overflow shows "+N more"
  const rightColContentHeight = top5HeaderHeight + top5ChartHeight + 20 + catchmentMaxHeight;
  const mapHeight = 170; // fixed — the map's own aspect-fit handles the rest
  const leftColContentHeight = mapHeight + 30; // + legend row
  const boxHeight = boxPadding * 2 + Math.max(leftColContentHeight, rightColContentHeight);

  const sectionHeaderHeight = 20;
  const totalSectionHeight = sectionHeaderHeight + statCardHeight + cardGap + boxHeight + 20;
  const adjustedStartY = ensureSpace(doc, startY, totalSectionHeight);

  let y = drawSectionHeader(
    doc,
    x,
    adjustedStartY,
    contentWidth,
    '04',
    'Districts/IDPs/Catchments at risk of eviction.',
    buildSubtitle(filters, resolvedMonth, firstMonth, reason),
  );

  drawStatCard(doc, {
    rect: { x, y, width: cardWidth3, height: statCardHeight },
    label: districtsLabel,
    value: formatNumber(metrics.districtsAtRiskCount),
    icon: pinIcon,
    iconImage: icons?.districts,
  });
  // TODO: placeholder third stat — swap for whatever metric you define.
  // Currently shows total households at risk across all districts.
  drawStatCard(doc, {
    rect: { x: x + cardWidth3 + cardGap, y, width: cardWidth3, height: statCardHeight },
    label: totalHouseholdsLabel,
    value: formatNumber(metrics.totalHouseholdsAtRisk),
    icon: houseIcon,
    iconImage: icons?.totalHouseholds,
  });
  drawStatCard(doc, {
    rect: { x: x + (cardWidth3 + cardGap) * 2, y, width: cardWidth3, height: statCardHeight },
    label: extremeLabel,
    value: formatNumber(metrics.bandCounts.extreme),
    description: extremeDescription,
    fillColor: colors.extremeBg,
    labelColor: colors.extreme700,
    valueColor: colors.extreme700,
    icon: warningIcon,
    iconColor: colors.extreme700,
    iconImage: icons?.extreme,
  });

  y += statCardHeight + cardGap;

  // --- Risk detail box: map (left) + top-5 chart & catchment areas (right).
  // The "current risk picture" grid that used to sit above the top-5 chart
  // is gone — top-5 moved up to fill that space, and the catchment-areas
  // list now fills where top-5 used to be. ---
  doc.setDrawColor(...colors.stone200);
  doc.setLineWidth(0.5);
  doc.roundedRect(x, y, contentWidth, boxHeight, layout.cardRadius, layout.cardRadius, 'D');

  const innerX = x + boxPadding;
  const innerY = y + boxPadding;

  // Left: real choropleth when geojson is available, dashed placeholder otherwise
  const mapRect = { x: innerX, y: innerY, width: leftColWidth, height: mapHeight };

  let unmatchedDistricts: string[] = [];
  const lookup = buildDistrictLookup(mapSnapshot);
  if (geojson && resolvedMonth) {
    const getBandForMap = (districtName: string): RiskBand | null => {
      const stats = lookup(districtName);
      if (!stats || stats.total_individuals_at_risk <= 0) return null; // treat 0 as no-data, not "low"
      return getRiskBand(stats.total_individuals_at_risk);
    };
    const { matchedDistricts } = drawDistrictMap(doc, {
      rect: mapRect,
      featureCollection: geojson,
      getBand: getBandForMap,
    });
    const riskDistrictNames = Object.keys(mapSnapshot).filter((d) => mapSnapshot[d].total_individuals_at_risk > 0);
    const matchedLower = new Set(matchedDistricts.map((n) => n.toLowerCase()));
    unmatchedDistricts = riskDistrictNames.filter((name) => {
      const lower = name.toLowerCase();
      if (matchedLower.has(lower)) return false;
      // Reverse-check the alias table too: matchedDistricts carries GeoJSON
      // spelling ("HELIWA"), riskDistrictNames carries API spelling
      // ("Hiliwa") — without this, an alias that correctly shades the map
      // still gets reported as unmatched here.
      const reverseAliased = Object.entries(DISTRICT_NAME_ALIASES).find(([, v]) => v === lower)?.[0];
      if (reverseAliased && matchedLower.has(reverseAliased)) return false;
      return true;
    });
    if (unmatchedDistricts.length > 0) {
      console.warn(
        `[areasAtRiskSection] ${unmatchedDistricts.length} at-risk district(s) have no matching boundary in the GeoJSON and could not be shaded on the map: ${unmatchedDistricts.join(', ')}`,
      );
    }
  } else {
    drawMapPlaceholder(
      doc,
      mapRect,
      resolvedMonth ? 'Pending district boundary data' : 'No risk data for this period',
    );
  }

  drawLegend(doc, innerX, innerY + mapHeight + 18, [
    { color: colors.low700, label: 'Low' },
    { color: colors.medium700, label: 'Medium' },
    { color: colors.high700, label: 'High' },
    { color: colors.extreme700, label: 'Extreme' },
  ]);
  if (unmatchedDistricts.length > 0) {
    doc.setFont(fonts.body, 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(...colors.stone400);
    const note = `Not shown (no boundary data): ${unmatchedDistricts.join(', ')}`;
    doc.text(note, innerX, innerY + mapHeight + 30, { maxWidth: leftColWidth });
  }

  // Right, top: top-5 chart (moved up from where the risk-picture grid used to be)
  const rightX = innerX + leftColWidth + colGap;

  doc.setFont(fonts.body, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...colors.stone500);
  doc.text('Top 5 highest-risk districts', rightX, innerY + 4);

  drawHorizontalBarChart(doc, {
    rect: { x: rightX, y: innerY + top5HeaderHeight, width: rightColWidth, height: top5ChartHeight },
    items: metrics.top5.map((d) => ({
      label: d.district,
      value: d.individualsAtRisk,
      displayValue: formatNumber(d.individualsAtRisk),
      color: BAND_COLOR[d.band],
    })),
  });

  // Right, below: catchment areas per district (fills the space top-5 used to occupy)
  const catchmentY = innerY + top5HeaderHeight + top5ChartHeight + 20;
  doc.setDrawColor(...colors.stone200);
  doc.setLineWidth(0.5);
  doc.line(rightX, catchmentY - 10, rightX + rightColWidth, catchmentY - 10);

  doc.setFont(fonts.body, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...colors.stone500);
  doc.text('Catchment areas at risk, by district', rightX, catchmentY);

  const catchmentEntries = Object.entries(mapSnapshot)
    .filter(([, stats]) => stats.total_individuals_at_risk > 0)
    .map(([district, stats]) => ({
      district,
      individualsAtRisk: stats.total_individuals_at_risk,
      areas: stats.catchment_areas_at_risk ?? ['N/A'],
    }));

  drawCatchmentAreasList(
    doc,
    rightX,
    catchmentY + 12,
    rightColWidth,
    catchmentMaxHeight + 24,
    catchmentEntries,
  );

  return y + boxHeight + 20; // gap before next section
}